import type { FC } from 'react';
import { FaChevronDown } from '../../../components/icons';
import { cn } from '../../../utils/cn';
import { WikiMarkdown, type WikiMarkdownInsert } from '../markdown/WikiMarkdown';
import { type WikiBlock } from '../markdown/parseWikiMarkdown';
import { WikiStickySide } from './WikiStickySide';
import type { WikiT } from '../wikiText';
import type { WikiPageText } from '../wiki.types';
import { WikiSection } from './WikiLayout';
import { WikiName } from './WikiName';
import { wikiProse } from '../wikiTypography';
import type { Language } from '../../../i18n/types';

export const WikiIntro: FC<{ text: string; className?: string }> = ({ text, className }) => (
  <p className={className ?? 'm-0 text-[17px] leading-[26px] text-label-primary'}>
    <WikiName name={wikiProse(text)} mode="text" />
  </p>
);

export const WIKI_TEXT_WIDTH = 'max-w-[60ch]';

export const WikiFaq: FC<{ items: WikiPageText['faq']; t: WikiT; className?: string }> = ({ items, t, className }) => (
  <WikiSection id="wiki-faq" title={t('faqTitle')} className={cn('scroll-mt-4', WIKI_TEXT_WIDTH, className)}>
    <div className="flex flex-col gap-2">
      {items.map((item) => (
        <details key={item.q} className="group rounded-[18px] bg-bg-secondary px-4 [&_summary::-webkit-details-marker]:hidden">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3.5 text-[16px] font-semibold leading-[22px] text-label-primary">
            <h3 className="m-0 text-[16px] font-semibold leading-[22px]">{wikiProse(item.q)}</h3>
            <FaChevronDown aria-hidden size={14} className="shrink-0 text-label-tertiary transition-transform group-open:rotate-180" />
          </summary>
          <p className="m-0 pb-4 text-[15px] leading-[23px] text-white/90">
            <WikiName name={wikiProse(item.a)} mode="text" />
          </p>
        </details>
      ))}
    </div>
  </WikiSection>
);

export const WikiArticleBody: FC<{ blocks: WikiBlock[]; lang: Language; insert?: WikiMarkdownInsert; className?: string }> = ({
  blocks,
  lang,
  insert,
  className,
}) => (
  <article className={cn(WIKI_TEXT_WIDTH, className)}>
    <WikiMarkdown blocks={blocks} lang={lang} insert={insert} />
  </article>
);

// WHY: в сетке страницы статья занимает левую колонку, вопросы — правую и липнут при прокрутке; без вопросов статья во всю сетку, но строка не длиннее ~760 px; оглавление только в руководствах
export const WikiArticleAside: FC<{ faq: WikiPageText['faq']; t: WikiT; className?: string }> = ({ faq, t, className }) => (
  <WikiStickySide className={className}>
    <WikiFaq items={faq} t={t} className="max-w-none" />
  </WikiStickySide>
);

export const WikiArticleAndFaq: FC<{ blocks: WikiBlock[]; faq: WikiPageText['faq']; lang: Language; t: WikiT }> = ({ blocks, faq, lang, t }) => {
  if (blocks.length === 0) return faq.length > 0 ? <WikiFaq items={faq} t={t} className="lg:col-start-1 lg:max-w-none" /> : null;
  const aside = faq.length > 0;
  return (
    <>
      <WikiArticleBody blocks={blocks} lang={lang} className={aside ? 'lg:col-start-1 lg:max-w-none' : 'lg:col-span-2 lg:max-w-[760px]'} />
      {aside && <WikiArticleAside faq={faq} t={t} className="lg:col-start-2" />}
    </>
  );
};
